import React, { useEffect, useState } from 'react';
import { Card, CardHeader } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Checkbox } from '../../components/ui/Checkbox';
import { Select } from '../../components/ui/Select';
import { Table } from '../../components/ui/Table';
import { CustomerSelect } from '../../components/domain/CustomerSelect';
import { formatCurrency } from '../../utils/formatters';
import { callBillingAPI } from '../../utils/api';
import { PlusIcon, EditIcon, TrashIcon, AlertCircleIcon, ArrowLeftIcon, Share2Icon, SaveIcon, PlayIcon, CheckCircleIcon, CreditCardIcon, XCircleIcon } from 'lucide-react';
interface SubscriptionTemplate {
  id: string;
  name: string;
  cycle: string;
  cycle_num: number;
  amount: number;
  link: string;
}
interface RunTemplatePaymentMethod {
  id: string;
  card_brand: string;
  card_exp: string;
  card_last_digits: string;
}
export interface EditCompanySubscriptionsProps {
  companyId: string;
  companyTaxRate: number;
  selectedCompanyId: string;
}
const SKIP_PAYMENT_ID = '__skip_payment__';
const formatBillingCycle = (cycle: string, cycleNum: number): string => {
  const cycleNames: Record<string, {
    singular: string;
    plural: string;
  }> = {
    week: {
      singular: 'Week',
      plural: 'Weeks'
    },
    month: {
      singular: 'Month',
      plural: 'Months'
    },
    year: {
      singular: 'Year',
      plural: 'Years'
    }
  };
  const cycleName = cycleNames[cycle] || {
    singular: cycle,
    plural: cycle
  };
  if (cycleNum === 1) return `Every ${cycleName.singular}`;
  return `Every ${cycleNum} ${cycleName.plural}`;
};
export function EditCompanySubscriptions({
  companyId,
  companyTaxRate,
  selectedCompanyId
}: EditCompanySubscriptionsProps) {
  // List state
  const [subscriptionTemplates, setSubscriptionTemplates] = useState<SubscriptionTemplate[]>([]);
  const [isLoadingSubTemplates, setIsLoadingSubTemplates] = useState(false);
  const [subTemplatesError, setSubTemplatesError] = useState<string | null>(null);
  // Share modal
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  // Create/Edit form
  const [showCreateSubTemplate, setShowCreateSubTemplate] = useState(false);
  const [editingSubTemplateId, setEditingSubTemplateId] = useState<string | null>(null);
  const [isLoadingEditSubTemplate, setIsLoadingEditSubTemplate] = useState(false);
  const [editSubTemplateError, setEditSubTemplateError] = useState<string | null>(null);
  const [subTemplateName, setSubTemplateName] = useState('');
  const [subTemplateCycle, setSubTemplateCycle] = useState('month');
  const [subTemplateCycleNum, setSubTemplateCycleNum] = useState(1);
  const [subTemplateInstallments, setSubTemplateInstallments] = useState('0');
  const [subTemplateTaxLabel, setSubTemplateTaxLabel] = useState('');
  const [subTemplateFee, setSubTemplateFee] = useState(0);
  const [subTemplateFeeLabel, setSubTemplateFeeLabel] = useState('Fee');
  const [subTemplateItems, setSubTemplateItems] = useState<{
    description: string;
    quantity: number;
    price: number;
    isTaxable: boolean;
  }[]>([{
    description: '',
    quantity: 1,
    price: 0,
    isTaxable: true
  }]);
  const [subTemplateErrors, setSubTemplateErrors] = useState<Record<string, string>>({});
  const [isSavingSubTemplate, setIsSavingSubTemplate] = useState(false);
  // Delete
  const [deleteSubTemplateId, setDeleteSubTemplateId] = useState<string | null>(null);
  const [isDeletingSubTemplate, setIsDeletingSubTemplate] = useState(false);
  // Run template
  const [runTemplateId, setRunTemplateId] = useState<string | null>(null);
  const [runTemplateCustomerId, setRunTemplateCustomerId] = useState('');
  const [runTemplateStartDate, setRunTemplateStartDate] = useState('');
  const [isRunningTemplate, setIsRunningTemplate] = useState(false);
  const [runTemplateError, setRunTemplateError] = useState<string | null>(null);
  const [runTemplateSuccessNumber, setRunTemplateSuccessNumber] = useState<string | null>(null);
  const [runTemplatePaymentMethods, setRunTemplatePaymentMethods] = useState<RunTemplatePaymentMethod[]>([]);
  const [runTemplateSelectedPaymentMethodId, setRunTemplateSelectedPaymentMethodId] = useState('');
  const [isLoadingRunTemplatePaymentMethods, setIsLoadingRunTemplatePaymentMethods] = useState(false);
  const isRunTemplateStartDateFuture = (() => {
    if (!runTemplateStartDate) return false;
    const start = new Date(runTemplateStartDate);
    start.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return start.getTime() > now.getTime();
  })();
  // Fetch templates on mount
  const fetchSubscriptionTemplates = async () => {
    setIsLoadingSubTemplates(true);
    setSubTemplatesError(null);
    try {
      const response = await callBillingAPI('getSubscriptionTemplates', {
        company_id: companyId
      });
      const templates: SubscriptionTemplate[] = Array.isArray(response) ? response : response.items || response.data || [];
      setSubscriptionTemplates(templates);
    } catch (error) {
      setSubTemplatesError(error instanceof Error ? error.message : 'Failed to load subscription templates');
    } finally {
      setIsLoadingSubTemplates(false);
    }
  };
  useEffect(() => {
    fetchSubscriptionTemplates();
  }, [companyId]);
  // When start date becomes future, clear skip payment
  useEffect(() => {
    if (isRunTemplateStartDateFuture && runTemplateSelectedPaymentMethodId === SKIP_PAYMENT_ID) {
      setRunTemplateSelectedPaymentMethodId(runTemplatePaymentMethods.length > 0 ? runTemplatePaymentMethods[0].id : '');
    }
  }, [isRunTemplateStartDateFuture, runTemplateSelectedPaymentMethodId, runTemplatePaymentMethods]);
  // Fetch payment methods when customer selected
  useEffect(() => {
    const fetchPM = async () => {
      if (!runTemplateCustomerId || !runTemplateId) {
        setRunTemplatePaymentMethods([]);
        setRunTemplateSelectedPaymentMethodId('');
        return;
      }
      setIsLoadingRunTemplatePaymentMethods(true);
      try {
        const response = await callBillingAPI('getCustomerPaymentMethods', {
          customer_id: runTemplateCustomerId
        });
        let methods: RunTemplatePaymentMethod[] = [];
        if (Array.isArray(response)) methods = response;else if (response.items && Array.isArray(response.items)) methods = response.items;else if (response.data && Array.isArray(response.data)) methods = response.data;
        setRunTemplatePaymentMethods(methods);
        setRunTemplateSelectedPaymentMethodId(methods.length > 0 ? methods[0].id : '');
      } catch {
        setRunTemplatePaymentMethods([]);
        setRunTemplateSelectedPaymentMethodId('');
      } finally {
        setIsLoadingRunTemplatePaymentMethods(false);
      }
    };
    fetchPM();
  }, [runTemplateCustomerId, runTemplateId]);
  // Handlers
  const handleShareTemplate = (template: SubscriptionTemplate) => {
    setShareLink(template.link || null);
    setShareModalOpen(true);
    setCopySuccess(false);
  };
  const handleCopyLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopySuccess(true);
      setTimeout(() => {
        setShareModalOpen(false);
        setCopySuccess(false);
      }, 1500);
    } catch {
      alert('Please copy this link manually: ' + shareLink);
    }
  };
  const handleViewLink = () => {
    if (!shareLink) return;
    window.open(shareLink, '_blank');
    setShareModalOpen(false);
  };
  const openCreateSubTemplate = () => {
    setSubTemplateName('');
    setSubTemplateCycle('month');
    setSubTemplateCycleNum(1);
    setSubTemplateInstallments('0');
    setSubTemplateTaxLabel(companyTaxRate > 0 ? `Tax (${Math.round(companyTaxRate)}%)` : 'Tax');
    setSubTemplateFee(0);
    setSubTemplateFeeLabel('Fee');
    setSubTemplateItems([{
      description: '',
      quantity: 1,
      price: 0,
      isTaxable: true
    }]);
    setSubTemplateErrors({});
    setShowCreateSubTemplate(true);
  };
  const closeCreateSubTemplate = () => {
    setShowCreateSubTemplate(false);
    setSubTemplateErrors({});
  };
  const addSubTemplateItem = () => {
    setSubTemplateItems([...subTemplateItems, {
      description: '',
      quantity: 1,
      price: 0,
      isTaxable: true
    }]);
  };
  const removeSubTemplateItem = (index: number) => {
    setSubTemplateItems(subTemplateItems.filter((_, i) => i !== index));
  };
  const updateSubTemplateItem = (index: number, updates: Partial<{
    description: string;
    quantity: number;
    price: number;
    isTaxable: boolean;
  }>) => {
    setSubTemplateItems(subTemplateItems.map((item, i) => i === index ? {
      ...item,
      ...updates
    } : item));
  };
  const validateSubTemplateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!subTemplateName.trim()) errors.name = 'Template name is required';
    if (subTemplateItems.length === 0) errors.items = 'Please add at least one item';
    if (subTemplateItems.some(item => !item.description.trim())) errors.items = 'Please fill in all item descriptions';
    setSubTemplateErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const buildApiParams = () => {
    const subtotal = subTemplateItems.reduce((sum, item) => sum + item.quantity * item.price, 0);
    const taxableSubtotal = subTemplateItems.filter(item => item.isTaxable).reduce((sum, item) => sum + item.quantity * item.price, 0);
    const taxAmount = taxableSubtotal * (companyTaxRate / 100);
    const grandTotal = subtotal + taxAmount + subTemplateFee;
    const items = subTemplateItems.map(item => ({
      title: item.description,
      qty: item.quantity,
      price: item.price,
      amount: item.quantity * item.price,
      taxable: item.isTaxable ? 1 : 0
    }));
    return {
      items,
      amount: grandTotal,
      tax: {
        amount: taxAmount,
        label: subTemplateTaxLabel || 'Tax'
      },
      fee: {
        amount: subTemplateFee,
        label: subTemplateFeeLabel || 'Fee'
      },
      cycle: subTemplateCycle,
      cycle_num: subTemplateCycleNum,
      installments: parseInt(subTemplateInstallments)
    };
  };
  const handleSaveSubTemplate = async () => {
    if (!validateSubTemplateForm()) return;
    setIsSavingSubTemplate(true);
    setSubTemplateErrors({});
    try {
      await callBillingAPI('createSubscriptionTemplate', {
        company_id: companyId,
        name: subTemplateName.trim(),
        ...buildApiParams()
      });
      setShowCreateSubTemplate(false);
      await fetchSubscriptionTemplates();
    } catch (error) {
      setSubTemplateErrors({
        submit: error instanceof Error ? error.message : 'Failed to create subscription template'
      });
    } finally {
      setIsSavingSubTemplate(false);
    }
  };
  const openEditSubTemplate = async (templateId: string) => {
    setEditingSubTemplateId(templateId);
    setIsLoadingEditSubTemplate(true);
    setEditSubTemplateError(null);
    setShowCreateSubTemplate(false);
    setSubTemplateErrors({});
    try {
      const response = await callBillingAPI('getSubscriptionTemplate', {
        id: templateId
      });
      setSubTemplateName(response.name || '');
      setSubTemplateCycle(response.cycle || 'month');
      setSubTemplateCycleNum(parseInt(String(response.cycle_num)) || 1);
      setSubTemplateInstallments(String(parseInt(String(response.installments)) || 0));
      setSubTemplateTaxLabel(response.tax_label || (companyTaxRate > 0 ? `Tax (${Math.round(companyTaxRate)}%)` : 'Tax'));
      setSubTemplateFee(parseFloat(String(response.fee)) || 0);
      setSubTemplateFeeLabel(response.fee_label || 'Fee');
      if (response.items && Array.isArray(response.items)) {
        const transformed = response.items.map((item: any) => ({
          description: item.title || '',
          quantity: parseFloat(String(item.qty)) || 1,
          price: parseFloat(String(item.price)) || 0,
          isTaxable: item.taxable === 1 || item.taxable === true
        }));
        setSubTemplateItems(transformed.length > 0 ? transformed : [{
          description: '',
          quantity: 1,
          price: 0,
          isTaxable: true
        }]);
      } else {
        setSubTemplateItems([{
          description: '',
          quantity: 1,
          price: 0,
          isTaxable: true
        }]);
      }
    } catch (error) {
      setEditSubTemplateError(error instanceof Error ? error.message : 'Failed to load template details');
    } finally {
      setIsLoadingEditSubTemplate(false);
    }
  };
  const closeEditSubTemplate = () => {
    setEditingSubTemplateId(null);
    setEditSubTemplateError(null);
    setSubTemplateErrors({});
  };
  const handleUpdateSubTemplate = async () => {
    if (!validateSubTemplateForm() || !editingSubTemplateId) return;
    setIsSavingSubTemplate(true);
    setSubTemplateErrors({});
    try {
      await callBillingAPI('editSubscriptionTemplate', {
        id: editingSubTemplateId,
        name: subTemplateName.trim(),
        ...buildApiParams()
      });
      setEditingSubTemplateId(null);
      await fetchSubscriptionTemplates();
    } catch (error) {
      setSubTemplateErrors({
        submit: error instanceof Error ? error.message : 'Failed to update subscription template'
      });
    } finally {
      setIsSavingSubTemplate(false);
    }
  };
  const handleDeleteSubTemplate = async () => {
    if (!deleteSubTemplateId) return;
    setIsDeletingSubTemplate(true);
    try {
      await callBillingAPI('deleteSubscriptionTemplate', {
        id: deleteSubTemplateId
      });
      setDeleteSubTemplateId(null);
      await fetchSubscriptionTemplates();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to delete template');
      setDeleteSubTemplateId(null);
    } finally {
      setIsDeletingSubTemplate(false);
    }
  };
  const openRunTemplateModal = (templateId: string) => {
    setRunTemplateId(templateId);
    setRunTemplateCustomerId('');
    setRunTemplateError(null);
    setRunTemplatePaymentMethods([]);
    setRunTemplateSelectedPaymentMethodId('');
    setIsLoadingRunTemplatePaymentMethods(false);
    const today = new Date();
    setRunTemplateStartDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
  };
  const handleRunTemplate = async () => {
    if (!runTemplateId || !runTemplateCustomerId) {
      setRunTemplateError('Please select a customer');
      return;
    }
    if (!runTemplateStartDate) {
      setRunTemplateError('Please select a start date');
      return;
    }
    setIsRunningTemplate(true);
    setRunTemplateError(null);
    try {
      const [year, month, day] = runTemplateStartDate.split('-');
      const apiParams: Record<string, any> = {
        id: runTemplateId,
        customer_id: runTemplateCustomerId,
        start_date: `${month}-${day}-${year}`
      };
      if (runTemplatePaymentMethods.length === 0 || runTemplateSelectedPaymentMethodId === SKIP_PAYMENT_ID) apiParams.ignore_auto_pay = 1;else if (runTemplateSelectedPaymentMethodId) apiParams.customer_payment_method_id = runTemplateSelectedPaymentMethodId;
      const newSubscr = await callBillingAPI('runSubscriptionTemplate', apiParams);
      setRunTemplateId(null);
      setRunTemplateSuccessNumber(newSubscr.number || newSubscr.id || 'N/A');
    } catch (error) {
      setRunTemplateError(error instanceof Error ? error.message : 'Failed to create subscription');
    } finally {
      setIsRunningTemplate(false);
    }
  };
  const getCardBrandDisplay = (brand: string) => {
    const brandUpper = brand?.toUpperCase() || 'CARD';
    const brandColors: Record<string, string> = {
      VISA: 'bg-blue-600',
      MASTERCARD: 'bg-red-500',
      AMEX: 'bg-blue-400',
      DISCOVER: 'bg-orange-500'
    };
    return {
      name: brandUpper,
      color: brandColors[brandUpper] || 'bg-gray-600'
    };
  };
  const subscriptionTemplateColumns = [{
    key: 'name',
    header: 'Name',
    render: (t: SubscriptionTemplate) => <span className="font-medium text-gray-900" data-id="element-2788">{t.name || '—'}</span>
  }, {
    key: 'cycle',
    header: 'Billing Cycle',
    render: (t: SubscriptionTemplate) => <span className="text-gray-700" data-id="element-2789">
          {formatBillingCycle(t.cycle, parseInt(String(t.cycle_num)) || 1)}
        </span>
  }, {
    key: 'amount',
    header: 'Amount',
    render: (t: SubscriptionTemplate) => <span className="font-medium text-gray-900" data-id="element-2790">
          {formatCurrency(parseFloat(String(t.amount)) || 0)}
        </span>
  }, {
    key: 'actions',
    header: '',
    render: (t: SubscriptionTemplate) => <div className="flex items-center justify-end gap-1" data-id="element-2791">
          <Button variant="ghost" size="sm" onClick={() => openRunTemplateModal(t.id)} className="text-gray-600 hover:text-green-600 hover:bg-green-50" aria-label="Run template" title="Create subscription from template" data-id="element-2792">
            <PlayIcon className="w-4 h-4" data-id="element-2793" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleShareTemplate(t)} className="text-gray-600 hover:text-blue-600 hover:bg-blue-50" aria-label="Share link" title="Share link" disabled={!t.link} data-id="element-2794">
            <Share2Icon className="w-4 h-4" data-id="element-2795" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => openEditSubTemplate(t.id)} aria-label="Edit template" title="Edit template" data-id="element-2796">
            <EditIcon className="w-4 h-4" data-id="element-2797" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDeleteSubTemplateId(t.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" aria-label="Delete template" title="Delete template" data-id="element-2798">
            <TrashIcon className="w-4 h-4" data-id="element-2799" />
          </Button>
        </div>
  }];
  // Create/Edit form view
  if (showCreateSubTemplate || editingSubTemplateId) {
    if (isLoadingEditSubTemplate) {
      return <Card data-id="element-2800">
          <div className="p-12 text-center" data-id="element-2801">
            <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-2802"></div>
            <p className="mt-4 text-sm text-gray-500" data-id="element-2803">
              Loading template details...
            </p>
          </div>
        </Card>;
    }
    if (editSubTemplateError) {
      return <Card data-id="element-2804">
          <div className="p-12 text-center" data-id="element-2805">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4" data-id="element-2806">
              <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-2807" />
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-2808">
              Failed to load template
            </p>
            <p className="text-sm text-gray-500 mb-4" data-id="element-2809">{editSubTemplateError}</p>
            <div className="flex justify-center gap-3" data-id="element-2810">
              <Button variant="secondary" size="sm" onClick={closeEditSubTemplate} data-id="element-2811">
                Back to List
              </Button>
              <Button size="sm" onClick={() => editingSubTemplateId && openEditSubTemplate(editingSubTemplateId)} data-id="element-2812">
                Retry
              </Button>
            </div>
          </div>
        </Card>;
    }
    return <div className="space-y-6" data-id="element-2813">
        <div className="flex items-center gap-3" data-id="element-2814">
          <Button variant="ghost" size="sm" onClick={() => {
          if (editingSubTemplateId) closeEditSubTemplate();else closeCreateSubTemplate();
        }} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-2816" />} data-id="element-2815">
            Back to Templates
          </Button>
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-2817">
            {editingSubTemplateId ? 'Edit Subscription Template' : 'New Subscription Template'}
          </h2>
        </div>
        <Card data-id="element-2818">
          <CardHeader title="Template Details" data-id="element-2819" />
          <div className="space-y-4" data-id="element-2820">
            <Input label="Template Name" value={subTemplateName} onChange={e => setSubTemplateName(e.target.value)} error={subTemplateErrors.name} placeholder="e.g., Monthly Service Plan" data-id="element-2821" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-2822">
              <div className="grid grid-cols-2 gap-2" data-id="element-2823">
                <Input label="Every" type="number" min="1" value={subTemplateCycleNum} onChange={e => setSubTemplateCycleNum(Math.max(1, parseInt(e.target.value) || 1))} data-id="element-2824" />
                <Select label="Period" options={[{
                value: 'week',
                label: 'Week(s)'
              }, {
                value: 'month',
                label: 'Month(s)'
              }, {
                value: 'year',
                label: 'Year(s)'
              }]} value={subTemplateCycle} onChange={e => setSubTemplateCycle(e.target.value)} data-id="element-2825" />
              </div>
              <Select label="Installments" options={[{
              value: '0',
              label: 'Infinite'
            }, ...Array.from({
              length: 12
            }, (_, i) => ({
              value: String(i + 1),
              label: String(i + 1)
            }))]} value={subTemplateInstallments} onChange={e => setSubTemplateInstallments(e.target.value)} helperText="Number of billing cycles (Infinite = ongoing)" data-id="element-2826" />
            </div>
          </div>
        </Card>
        <Card data-id="element-2827">
          <CardHeader title="Recurring Items" subtitle="These items will be billed each cycle" action={<Button variant="secondary" size="sm" onClick={addSubTemplateItem} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2830" />} data-id="element-2829">
                Add Item
              </Button>} data-id="element-2828" />
          {subTemplateErrors.items && <p className="text-sm text-red-600 mb-4" data-id="element-2831">
              {subTemplateErrors.items}
            </p>}
          <div className="space-y-4" data-id="element-2832">
            {subTemplateItems.map((item, index) => <div key={index} className="bg-gray-50 rounded-lg p-4 space-y-4" data-id="element-2833">
                <div className="flex items-start justify-between gap-4" data-id="element-2834">
                  <span className="text-sm font-medium text-gray-500 mt-2" data-id="element-2835">
                    #{index + 1}
                  </span>
                  <div className="flex-1" data-id="element-2836">
                    <Input placeholder="Item description" value={item.description} onChange={e => updateSubTemplateItem(index, {
                  description: e.target.value
                })} data-id="element-2837" />
                  </div>
                  {subTemplateItems.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeSubTemplateItem(index)} className="text-red-600 hover:text-red-700 hover:bg-red-50" aria-label="Remove item" data-id="element-2838">
                      <TrashIcon className="w-4 h-4" data-id="element-2839" />
                    </Button>}
                </div>
                <div className="grid grid-cols-3 gap-4" data-id="element-2840">
                  <Input label="Quantity" type="number" min="1" step="1" value={item.quantity} onChange={e => updateSubTemplateItem(index, {
                quantity: Math.max(1, parseInt(e.target.value) || 1)
              })} data-id="element-2841" />
                  <Input label="Unit Price" type="number" min="0" step="0.01" value={item.price} onChange={e => updateSubTemplateItem(index, {
                price: Math.max(0, parseFloat(e.target.value) || 0)
              })} data-id="element-2842" />
                  <div className="flex flex-col justify-end" data-id="element-2843">
                    <Checkbox label="Taxable" checked={item.isTaxable} onChange={e => updateSubTemplateItem(index, {
                  isTaxable: e.target.checked
                })} data-id="element-2844" />
                  </div>
                </div>
                <div className="flex items-center pt-2 border-t border-gray-200 text-sm" data-id="element-2845">
                  <span className="text-gray-500" data-id="element-2846">Line Total:</span>
                  <span className="font-semibold text-gray-900 ml-2" data-id="element-2847">
                    {formatCurrency(item.quantity * item.price)}
                  </span>
                </div>
              </div>)}
          </div>
        </Card>
        <Card data-id="element-2848">
          <CardHeader title="Tax & Fee Settings" data-id="element-2849" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2850">
            <Input label="Tax Label" value={subTemplateTaxLabel} onChange={e => setSubTemplateTaxLabel(e.target.value)} placeholder="e.g., Tax (6%)" helperText={`Company tax rate (${Math.round(companyTaxRate)}%) applied to taxable items`} data-id="element-2851" />
            <Input label="Fee" type="number" min="0" step="0.01" value={subTemplateFee} onChange={e => setSubTemplateFee(parseFloat(e.target.value) || 0)} helperText="Additional fee charged each billing cycle" data-id="element-2852" />
            <Input label="Fee Label" value={subTemplateFeeLabel} onChange={e => setSubTemplateFeeLabel(e.target.value)} placeholder="e.g., Service Fee" data-id="element-2853" />
          </div>
        </Card>
        <Card data-id="element-2854">
          <CardHeader title="Per-Cycle Summary" data-id="element-2855" />
          <div className="space-y-2 text-sm" data-id="element-2856">
            <div className="flex justify-between" data-id="element-2857">
              <span className="text-gray-500" data-id="element-2858">Subtotal</span>
              <span className="text-gray-900" data-id="element-2859">
                {formatCurrency(subTemplateItems.reduce((sum, item) => sum + item.quantity * item.price, 0))}
              </span>
            </div>
            {companyTaxRate > 0 && <div className="flex justify-between" data-id="element-2860">
                <span className="text-gray-500" data-id="element-2861">
                  {subTemplateTaxLabel || 'Tax'}
                </span>
                <span className="text-gray-900" data-id="element-2862">
                  {formatCurrency(subTemplateItems.filter(item => item.isTaxable).reduce((sum, item) => sum + item.quantity * item.price, 0) * (companyTaxRate / 100))}
                </span>
              </div>}
            {subTemplateFee > 0 && <div className="flex justify-between" data-id="element-2863">
                <span className="text-gray-500" data-id="element-2864">
                  {subTemplateFeeLabel || 'Fee'}
                </span>
                <span className="text-gray-900" data-id="element-2865">
                  {formatCurrency(subTemplateFee)}
                </span>
              </div>}
            <div className="flex justify-between pt-2 border-t border-gray-200 font-semibold" data-id="element-2866">
              <span className="text-gray-900" data-id="element-2867">Total</span>
              <span className="text-gray-900" data-id="element-2868">
                {formatCurrency(subTemplateItems.reduce((sum, item) => sum + item.quantity * item.price, 0) + subTemplateItems.filter(item => item.isTaxable).reduce((sum, item) => sum + item.quantity * item.price, 0) * (companyTaxRate / 100) + subTemplateFee)}
              </span>
            </div>
          </div>
        </Card>
        {subTemplateErrors.submit && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-2869">
            <p className="text-sm text-red-600" data-id="element-2870">{subTemplateErrors.submit}</p>
          </div>}
        <div className="flex justify-end gap-3" data-id="element-2871">
          <Button variant="secondary" onClick={() => {
          if (editingSubTemplateId) closeEditSubTemplate();else closeCreateSubTemplate();
        }} disabled={isSavingSubTemplate} data-id="element-2872">
            Cancel
          </Button>
          <Button onClick={editingSubTemplateId ? handleUpdateSubTemplate : handleSaveSubTemplate} isLoading={isSavingSubTemplate} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-2874" />} data-id="element-2873">
            {editingSubTemplateId ? 'Save Changes' : 'Save Template'}
          </Button>
        </div>
      </div>;
  }
  // List view
  return <>
      <Card padding="none" data-id="element-2875">
        <div className="p-6 border-b border-gray-200" data-id="element-2876">
          <CardHeader title="Subscription Templates" subtitle="Manage reusable subscription templates with shareable links" action={<Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2879" />} onClick={openCreateSubTemplate} data-id="element-2878">
                Add Subscription Template
              </Button>} data-id="element-2877" />
        </div>
        {isLoadingSubTemplates ? <div className="p-12 text-center" data-id="element-2880">
            <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-2881"></div>
            <p className="mt-4 text-sm text-gray-500" data-id="element-2882">
              Loading subscription templates...
            </p>
          </div> : subTemplatesError ? <div className="p-12 text-center" data-id="element-2883">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4" data-id="element-2884">
              <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-2885" />
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-2886">
              Failed to load subscription templates
            </p>
            <p className="text-sm text-gray-500 mb-4" data-id="element-2887">{subTemplatesError}</p>
            <Button size="sm" onClick={fetchSubscriptionTemplates} data-id="element-2888">
              Retry
            </Button>
          </div> : <Table columns={subscriptionTemplateColumns} data={subscriptionTemplates} keyExtractor={t => t.id} emptyMessage="No subscription templates found" data-id="element-2889" />}
      </Card>

      {/* Share Modal */}
      <Modal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} title="Share Subscription Template" data-id="element-2890">
        <div className="space-y-4" data-id="element-2891">
          <p className="text-sm text-gray-600" data-id="element-2892">
            Share this subscription template using the link below:
          </p>
          {shareLink && <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-2893">
              <code className="text-xs text-gray-700 break-all" data-id="element-2894">
                {shareLink}
              </code>
            </div>}
          <div className="flex gap-3 pt-2" data-id="element-2895">
            <Button variant="primary" onClick={handleCopyLink} className="flex-1" data-id="element-2896">
              {copySuccess ? <>✓ Copied!</> : <>Copy Link</>}
            </Button>
            <Button variant="secondary" onClick={handleViewLink} className="flex-1" data-id="element-2897">
              View
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={!!deleteSubTemplateId} onClose={() => setDeleteSubTemplateId(null)} title="Delete Subscription Template" size="sm" data-id="element-2898">
        <p className="text-gray-600 mb-6" data-id="element-2899">
          Are you sure you want to delete this subscription template? This
          action cannot be undone.
        </p>
        <div className="flex justify-end gap-3" data-id="element-2900">
          <Button variant="secondary" onClick={() => setDeleteSubTemplateId(null)} data-id="element-2901">
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteSubTemplate} isLoading={isDeletingSubTemplate} data-id="element-2902">
            Delete
          </Button>
        </div>
      </Modal>

      {/* Run Template Modal */}
      <Modal isOpen={!!runTemplateId} onClose={() => {
      setRunTemplateId(null);
      setRunTemplateError(null);
    }} title="Create Subscription from Template" data-id="element-2903">
        <div className="space-y-4" data-id="element-2904">
          <p className="text-sm text-gray-600" data-id="element-2905">
            Select a customer to create a new subscription from this template.
          </p>
          <CustomerSelect value={runTemplateCustomerId} onChange={id => {
          setRunTemplateCustomerId(id);
          setRunTemplateError(null);
        }} error={runTemplateError && !runTemplateCustomerId ? runTemplateError : undefined} companyId={companyId || selectedCompanyId} data-id="element-2906" />
          <Input label="Start Date" type="date" value={runTemplateStartDate} onChange={e => {
          setRunTemplateStartDate(e.target.value);
          setRunTemplateError(null);
        }} min={(() => {
          const t = new Date();
          return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
        })()} data-id="element-2907" />
          {runTemplateCustomerId && <div data-id="element-2908">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2909">
                Payment Method
              </label>
              {isLoadingRunTemplatePaymentMethods ? <div className="text-center py-4" data-id="element-2910">
                  <div className="inline-block w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-1" data-id="element-2911"></div>
                  <p className="text-xs text-gray-500" data-id="element-2912">
                    Loading payment methods...
                  </p>
                </div> : runTemplatePaymentMethods.length === 0 ? <div className="text-center py-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-2913">
                  <CreditCardIcon className="w-6 h-6 text-gray-300 mx-auto mb-1" data-id="element-2914" />
                  <p className="text-xs text-gray-500" data-id="element-2915">
                    No saved payment methods
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5" data-id="element-2916">
                    Invoice will be created without auto-charge
                  </p>
                </div> : <div className="space-y-2" data-id="element-2917">
                  {runTemplatePaymentMethods.map(method => {
              const brand = getCardBrandDisplay(method.card_brand);
              const isSelected = runTemplateSelectedPaymentMethodId === method.id;
              return <div key={method.id} onClick={() => setRunTemplateSelectedPaymentMethodId(method.id)} className={`border rounded-lg p-3 transition-colors cursor-pointer ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2918">
                        <div className="flex items-center gap-3" data-id="element-2919">
                          <div className={`w-10 h-6 ${brand.color} rounded flex items-center justify-center text-white text-xs font-bold`} data-id="element-2920">
                            {brand.name.slice(0, 4)}
                          </div>
                          <div className="flex-1" data-id="element-2921">
                            <span className="text-sm text-gray-900" data-id="element-2922">
                              •••• {method.card_last_digits}
                            </span>
                            <p className="text-xs text-gray-500" data-id="element-2923">
                              Expires {method.card_exp}
                            </p>
                          </div>
                          {isSelected && <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center" data-id="element-2924">
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2925">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-2926" />
                              </svg>
                            </div>}
                        </div>
                      </div>;
            })}
                  {!isRunTemplateStartDateFuture && <div onClick={() => setRunTemplateSelectedPaymentMethodId(SKIP_PAYMENT_ID)} className={`border rounded-lg p-3 transition-colors cursor-pointer ${runTemplateSelectedPaymentMethodId === SKIP_PAYMENT_ID ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2927">
                      <div className="flex items-center gap-3" data-id="element-2928">
                        <div className="w-10 h-6 bg-amber-500 rounded flex items-center justify-center" data-id="element-2929">
                          <XCircleIcon className="w-4 h-4 text-white" data-id="element-2930" />
                        </div>
                        <div className="flex-1" data-id="element-2931">
                          <span className="text-sm text-gray-900" data-id="element-2932">
                            Don't pay the first invoice right now
                          </span>
                          <p className="text-xs text-gray-500" data-id="element-2933">
                            Invoice will be created but not charged
                          </p>
                        </div>
                        {runTemplateSelectedPaymentMethodId === SKIP_PAYMENT_ID && <div className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center" data-id="element-2934">
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2935">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-2936" />
                            </svg>
                          </div>}
                      </div>
                    </div>}
                </div>}
            </div>}
          {runTemplateError && runTemplateCustomerId && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-2937">
              <p className="text-sm text-red-600" data-id="element-2938">{runTemplateError}</p>
            </div>}
          <div className="flex justify-end gap-3 pt-2" data-id="element-2939">
            <Button variant="secondary" onClick={() => {
            setRunTemplateId(null);
            setRunTemplateError(null);
          }} data-id="element-2940">
              Cancel
            </Button>
            <Button onClick={handleRunTemplate} isLoading={isRunningTemplate} disabled={!runTemplateCustomerId} leftIcon={<PlayIcon className="w-4 h-4" data-id="element-2942" />} data-id="element-2941">
              Create Subscription
            </Button>
          </div>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal isOpen={!!runTemplateSuccessNumber} onClose={() => setRunTemplateSuccessNumber(null)} title="Subscription Created" size="sm" data-id="element-2943">
        <div className="text-center space-y-4" data-id="element-2944">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mx-auto" data-id="element-2945">
            <CheckCircleIcon className="w-6 h-6 text-green-600" data-id="element-2946" />
          </div>
          <div data-id="element-2947">
            <p className="text-gray-900 font-medium" data-id="element-2948">
              Subscription created successfully!
            </p>
            <p className="text-sm text-gray-500 mt-1" data-id="element-2949">
              Subscription number:{' '}
              <span className="font-semibold text-gray-900" data-id="element-2950">
                {runTemplateSuccessNumber}
              </span>
            </p>
          </div>
          <div className="pt-2" data-id="element-2951">
            <Button onClick={() => setRunTemplateSuccessNumber(null)} data-id="element-2952">
              OK
            </Button>
          </div>
        </div>
      </Modal>
    </>;
}